/**
 * <QuickPaymentDrawer> — canonical per AR-7. Uses Payments Engine only.
 */
import { useState } from "react";
import { toast } from "sonner";
import { OperationalDrawer, BottomActionBar, QuickActionGrid } from "@/components/operational";
import { CreditCard, Banknote, Smartphone, Landmark, Ticket, Building } from "lucide-react";
import { foApi, money , formatApiError } from "../foApi";
import { NumberInput } from "@/components/ui/number-input";

const METHODS = [
  { code: "cash",              label: "Cash",              icon: Banknote },
  { code: "card",              label: "Card",              icon: CreditCard },
  { code: "upi",               label: "UPI",               icon: Smartphone },
  { code: "bank_transfer",     label: "Bank Transfer",     icon: Landmark },
  { code: "voucher",           label: "Voucher",           icon: Ticket },
  { code: "corporate_credit",  label: "Corporate Credit",  icon: Building },
];

export default function QuickPaymentDrawer({ open, onOpenChange, folio, stay, onSuccess }) {
  const [method, setMethod] = useState(null);
  const [amount, setAmount] = useState("");
  const [ref, setRef] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!method) { toast.error("Choose payment method"); return; }
    const minor = Math.round(parseFloat(amount) * 100);
    if (!minor || minor <= 0) { toast.error("Enter a positive amount"); return; }
    setSaving(true);
    try {
      await foApi.quickPayment({
        folio_id: folio.id, stay_id: stay?.id,
        method: method.code, amount_minor: minor,
        instrument: ref ? { reference: ref } : {},
      });
      setMethod(null); setAmount(""); setRef("");
      onSuccess?.();
    } catch (e) {
      toast.error(formatApiError(e, "Payment failed"));
    } finally { setSaving(false); }
  };

  const balance = folio?.balance_minor_cache || 0;

  return (
    <OperationalDrawer open={open} onOpenChange={onOpenChange}
      title="Take Payment" subtitle={folio?.folio_no || "Folio"}
      statusChip={<span className="text-[13px] font-medium text-slate-600">Balance {money(balance)}</span>}
      size="md" testid="quickpayment-drawer"
      footer={
        <BottomActionBar
          primary={{ label: saving ? "Processing..." : "Record Payment",
                       icon: CreditCard, tone: "success", onClick: submit,
                       loading: saving, disabled: !method || !amount,
                       testid: "quickpayment-confirm" }}
          secondaryActions={[
            { label: "Cancel", tone: "secondary", onClick: () => onOpenChange(false), testid: "quickpayment-cancel" }
          ]}
        />
      }>
      <div className="p-4 space-y-4">
        <div>
          <div className="text-[12px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Method</div>
          <QuickActionGrid
            columns={3}
            actions={METHODS.map(m => ({
              id: m.code, label: m.label, icon: m.icon,
              tone: method?.code === m.code ? "active" : "ready",
              onClick: () => setMethod(m),
              testid: `qp-method-${m.code}`,
            }))}
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {[balance, Math.round(balance / 2), 100 * 100, 500 * 100, 1000 * 100].filter(x => x > 0).slice(0, 4).map((v, i) => (
            <button key={i}
              onClick={() => setAmount((v / 100).toFixed(2))}
              className="min-h-[44px] px-3 rounded-lg border-2 border-slate-300 hover:border-sky-500 text-[13px] font-medium"
              data-testid={`qp-preset-${i}`}>{money(v)}</button>
          ))}
        </div>
        <div>
          <label className="text-[12px] uppercase tracking-wider text-slate-500 font-semibold">Amount (₹)</label>
          <NumberInput min="0" step="0.01" value={amount}
                   onChange={e => setAmount(e.target.value)}
                   className="w-full mt-1 border-2 border-slate-300 rounded-lg p-3 text-[18px] font-semibold h-14"
                   placeholder="0.00"
                   data-testid="qp-amount"/>
        </div>
        {method && method.code !== "cash" && (
          <div>
            <label className="text-[12px] uppercase tracking-wider text-slate-500 font-semibold">Reference (optional)</label>
            <input type="text" value={ref} onChange={e => setRef(e.target.value)}
                     className="w-full mt-1 border-2 border-slate-300 rounded-lg p-2 text-[14px] h-12"
                     placeholder={method.code === "card" ? "Auth code…" : "Transaction ref…"}
                     data-testid="qp-ref"/>
          </div>
        )}
      </div>
    </OperationalDrawer>
  );
}
